import React, {
   createContext,
   useContext,
   useState,
   useEffect,
   useCallback,
   ReactNode,
   useRef,
} from 'react';
import { useUser } from './UserContext';
import webSocketService, {
   ChatMessage,
   ChatRoom,
   UserStatus,
   TypingIndicator,
   PaginatedRoomsResult,
} from '../services/WebSocketService';
import onlineStatusService from '../services/OnlineStatusService';
import { toast } from 'react-toastify';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Interface that matches the existing ChatContext for compatibility
export interface WebSocketChatContextType {
   // Connection state
   client: any; // Keep for compatibility, will be WebSocket service
   loading: boolean;
   error: Error | null;
   connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
   isFallbackMode: boolean;

   // Network status
   networkStatus: {
      isOnline: boolean;
      supported: boolean;
   };

   // Chat data
   channels: ChatRoom[];
   activeChannel: ChatRoom | null;
   messages: { [roomId: string]: ChatMessage[] };
   onlineUsers: UserStatus[];
   unreadCount: number;

   // Room pagination state
   roomsPagination: {
      hasMore: boolean;
      lastDoc?: any;
      loading: boolean;
   };

   // Loading states
   loadingStates: {
      sendingMessage: { [roomId: string]: boolean };
      addingReaction: { [messageId: string]: { [emoji: string]: boolean } };
      removingReaction: { [messageId: string]: { [emoji: string]: boolean } };
   };

   // Actions
   setActiveChannel: (channel: ChatRoom | null) => void;
   refreshChannels: () => Promise<void>;
   loadMoreRooms: () => Promise<void>;
   clearCacheAndRefresh: () => Promise<void>;
   cleanupInvalidRooms: () => Promise<void>;
   forceReconnection: () => Promise<void>;
   createOneOnOneChat: (userId: string) => Promise<ChatRoom | null>;
   createTripGroupChat: (tripId: string, tripTitle: string) => Promise<ChatRoom | null>;
   createEventGroupChat: (eventId: string) => Promise<ChatRoom | null>;
   sendMessage: (roomId: string, text: string) => Promise<void>;
   editMessage: (messageId: string, newText: string, roomId?: string) => Promise<void>;
   deleteMessage: (messageId: string, roomId?: string) => Promise<void>;
   reactToMessage: (messageId: string, emoji: string, roomId?: string) => Promise<void>;
   removeReaction: (messageId: string, emoji: string, roomId?: string) => Promise<void>;
   loadRoomMessages: (roomId: string) => Promise<void>;
   loadReactionsForRoom: (roomId: string, messageIds?: string[]) => Promise<void>;
   getMessagesBetweenUsers: (userId1: string, userId2: string) => Promise<ChatMessage[]>;

   // Typing indicators
   startTyping: (roomId: string) => void;
   stopTyping: (roomId: string) => void;
   typingUsers: { [roomId: string]: string[] };

   // Loading state helpers
   isSendingMessage: (roomId: string) => boolean;
   isAddingReaction: (messageId: string, emoji: string) => boolean;
   isRemovingReaction: (messageId: string, emoji: string) => boolean;

   // New method for loading more messages for pagination
   loadMoreMessages: (roomId: string, currentMessageCount: number) => Promise<number>;

   // Online status helpers
   getUserOnlineStatus: (userId: string) => Promise<UserStatus | null>;
   getMultipleUsersOnlineStatus: (userIds: string[]) => Promise<UserStatus[]>;
   isUserOnline: (userId: string) => boolean;
}

const WebSocketChatContext = createContext<WebSocketChatContextType | undefined>(undefined);

export const useWebSocketChat = () => {
   const context = useContext(WebSocketChatContext);
   if (!context) {
      throw new Error('useWebSocketChat must be used within a WebSocketChatProvider');
   }
   return context;
};

interface WebSocketChatProviderProps {
   children: React.ReactNode;
}

export const WebSocketChatProvider: React.FC<WebSocketChatProviderProps> = ({ children }) => {
   const { currentUser } = useUser();

   // State
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<Error | null>(null);
   const [connectionStatus, setConnectionStatus] = useState<
      'connecting' | 'connected' | 'disconnected' | 'error'
   >('disconnected');
   const [networkStatus, setNetworkStatus] = useState<{
      isOnline: boolean;
      supported: boolean;
   }>({
      isOnline: true,
      supported: false,
   });
   const [channels, setChannels] = useState<ChatRoom[]>([]);
   const [activeChannel, setActiveChannel] = useState<ChatRoom | null>(null);
   const [messages, setMessages] = useState<{ [roomId: string]: ChatMessage[] }>({});
   const [typingUsers, setTypingUsers] = useState<{ [roomId: string]: string[] }>({});
   const [unreadCount, setUnreadCount] = useState<number>(0);

   // Room pagination state
   const [roomsPagination, setRoomsPagination] = useState<{
      hasMore: boolean;
      lastDoc?: any;
      loading: boolean;
   }>({
      hasMore: true,
      lastDoc: undefined,
      loading: false,
   });

   // Loading states
   const [loadingStates, setLoadingStates] = useState<{
      sendingMessage: { [roomId: string]: boolean };
      addingReaction: { [messageId: string]: { [emoji: string]: boolean } };
      removingReaction: { [messageId: string]: { [emoji: string]: boolean } };
   }>({
      sendingMessage: {},
      addingReaction: {},
      removingReaction: {},
   });

   // Initialize network status
   useEffect(() => {
      const currentNetworkStatus = webSocketService.getNetworkStatus();
      setNetworkStatus(currentNetworkStatus);
   }, []);

   // Initialize WebSocket connection
   const initializeConnection = useCallback(async () => {
      if (!currentUser) return;

      try {
         setLoading(true);
         setConnectionStatus('connecting');
         setError(null);

         // First, try to load channels even without WebSocket connection
         await refreshChannels();

         // Then try to connect to WebSocket server (let service determine URL)
         await webSocketService.connect(
            currentUser.uid,
            currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
         );

         // Set up event listeners
         setupEventListeners();

         setConnectionStatus('connected');

         // Refresh channels again to join WebSocket rooms
         await refreshChannels();

         // Status updates are now handled by UserContext to prevent conflicts
      } catch (err) {
         console.error('❌ Failed to initialize WebSocket connection:', err);
         setError(err instanceof Error ? err : new Error('Connection failed'));
         setConnectionStatus('error');

         // Even if WebSocket fails, try to load channels from Firestore
         try {
            await refreshChannels();
         } catch (firestoreErr) {
            console.error('❌ Failed to load channels from Firestore:', firestoreErr);
         }
      } finally {
         setLoading(false);
      }
   }, [currentUser]);

   // Set up WebSocket event listeners
   const setupEventListeners = useCallback(() => {
      if (!webSocketService) return;

      // Connection status events
      webSocketService.on('connect', () => {
         setConnectionStatus('connected');
         setError(null);
      });

      webSocketService.on('disconnect', (reason: string) => {
         setConnectionStatus('disconnected');

         // Update error message based on network status
         if (reason === 'Network offline') {
            setError(
               new Error('You are offline. Connection will resume when network is restored.')
            );
         }
      });

      webSocketService.on('error', (error: Error) => {
         setError(error);
         setConnectionStatus('error');
      });

      // Network status events
      webSocketService.on(
         'network_status_changed',
         (status: { isOnline: boolean; supported: boolean }) => {
            setNetworkStatus(status);

            if (status.isOnline) {
               // Clear offline-related errors when coming back online
               setError(null);
            } else {
               // Set offline error when going offline
               setError(
                  new Error('You are offline. Connection will resume when network is restored.')
               );
            }
         }
      );

      // Loading state events
      webSocketService.on('loading-state-changed', (newLoadingStates) => {
         setLoadingStates(newLoadingStates);
      });

      // Reconnection events
      webSocketService.on('reconnecting', (attemptNumber) => {
         setConnectionStatus('connecting');

         // Clear offline errors during reconnection attempts
         if (networkStatus.isOnline) {
            setError(null);
         }
      });

      webSocketService.on('reconnected', (attemptNumber) => {
         setConnectionStatus('connected');
         setError(null);
         // Refresh channels after reconnection
         refreshChannels();
      });

      // Message events
      webSocketService.on('new-message', (message: ChatMessage) => {
         // Update channels with last message
         setChannels((prev) => {
            const updated = prev.map((channel) => {
               if (channel.id === message.roomId) {
                  return {
                     ...channel,
                     lastMessage: message,
                  };
               }
               return channel;
            });
            return updated;
         });

         // Add message to the messages state for real-time updates
         setMessages((prevMessages) => {
            const updatedMessages = { ...prevMessages };
            const roomId = message.roomId;

            // Initialize room messages array if it doesn't exist
            if (!updatedMessages[roomId]) {
               updatedMessages[roomId] = [];
            }

            // Check if message already exists to prevent duplicates
            const existingMessageIndex = updatedMessages[roomId].findIndex(
               (msg) => msg.id === message.id
            );

            if (existingMessageIndex === -1) {
               // Message doesn't exist, add it
               updatedMessages[roomId] = [...updatedMessages[roomId], message];
            } else {
               // Message exists, update it (in case of edits or reaction updates)
               updatedMessages[roomId][existingMessageIndex] = message;
            }

            return updatedMessages;
         });
      });

      // Reaction events
      webSocketService.on('message-reaction', (messageId, emoji, userId, added) => {
         setMessages((prevMessages) => {
            const updatedMessages = { ...prevMessages };

            // Find the message across all rooms
            let messageFound = false;
            for (const roomId in updatedMessages) {
               const messageIndex = updatedMessages[roomId].findIndex(
                  (msg) => msg.id === messageId
               );
               if (messageIndex !== -1) {
                  messageFound = true;
                  const message = updatedMessages[roomId][messageIndex];
                  const updatedMessage = { ...message };

                  // Initialize reactions if it doesn't exist
                  if (!updatedMessage.reactions) {
                     updatedMessage.reactions = {};
                  }

                  // Initialize emoji array if it doesn't exist
                  if (!updatedMessage.reactions[emoji]) {
                     updatedMessage.reactions[emoji] = [];
                  }

                  if (added) {
                     // Add reaction if user hasn't already reacted with this emoji
                     if (!updatedMessage.reactions[emoji].includes(userId)) {
                        updatedMessage.reactions[emoji] = [
                           ...updatedMessage.reactions[emoji],
                           userId,
                        ];
                     }
                  } else {
                     // Remove reaction
                     updatedMessage.reactions[emoji] = updatedMessage.reactions[emoji].filter(
                        (id) => id !== userId
                     );

                     // Remove emoji key if no users have this reaction
                     if (updatedMessage.reactions[emoji].length === 0) {
                        delete updatedMessage.reactions[emoji];
                     }
                  }

                  // Update the message in the array
                  updatedMessages[roomId][messageIndex] = updatedMessage;
                  break;
               }
            }

            if (!messageFound) {
               console.warn(`⚠️ Message ${messageId} not found in any room for reaction update`);
            }

            return updatedMessages;
         });
      });

      // Typing events
      webSocketService.on('user-typing', (indicator: TypingIndicator) => {
         setTypingUsers((prev) => ({
            ...prev,
            [indicator.roomId]: [...(prev[indicator.roomId] || []), indicator.userId].filter(
               (id, index, arr) => arr.indexOf(id) === index
            ),
         }));
      });

      webSocketService.on('user-stopped-typing', (userId: string, roomId: string) => {
         setTypingUsers((prev) => ({
            ...prev,
            [roomId]: (prev[roomId] || []).filter((id) => id !== userId),
         }));
      });

      // Group chat notifications
      webSocketService.on('group-chat-notification', (notification) => {
         if (notification.type === 'group_chat_created') {
            // Refresh the channels list to show the new group chat
            refreshChannels().catch((err) => {
               console.error('Failed to refresh channels after group chat notification:', err);
            });

            // Optionally show a toast notification
            toast.info(`You've been added to "${notification.entityName}" group chat`);
         }
      });

      // User status events
      webSocketService.on('user-status-changed', (status: UserStatus) => {
         onlineStatusService.updateUserStatusFromEvent(status);
      });

      // Error handling
      webSocketService.on('error', (error: Error) => {
         console.error('WebSocket error:', error);
         setError(error);
      });
   }, []);

   // Refresh channels
   const refreshChannels = useCallback(async () => {
      try {
         // Get current user ID
         if (!currentUser) {
            console.warn('⚠️ No current user available for refreshing channels');
            return;
         }

         // Set loading state for rooms pagination
         setRoomsPagination((prev) => ({ ...prev, loading: true }));

         // Use the paginated Firebase method to load initial rooms
         const roomsResult = await webSocketService.getRoomsFromFirebase(currentUser.uid, 20);

         // Set the rooms and pagination state
         setChannels(roomsResult.rooms);
         setRoomsPagination({
            hasMore: roomsResult.hasMore,
            lastDoc: roomsResult.lastDoc,
            loading: false,
         });

         // Calculate total unread count
         const totalUnread = roomsResult.rooms.reduce((acc: number, room: ChatRoom) => {
            return acc + (room.unreadCount || 0);
         }, 0);
         setUnreadCount(totalUnread);

         // Load avatars for group chats lazily in the background (non-blocking)
         if (roomsResult.rooms.length > 0) {
            webSocketService
               .loadAvatarsForRooms(roomsResult.rooms, currentUser.uid)
               .then(() => {
                  // Update the channels state with the loaded avatars
                  setChannels([...roomsResult.rooms]);
               })
               .catch((err) => {
                  console.warn('Failed to load avatars:', err);
               });
         }

         // Only try to join WebSocket rooms if we have a connected WebSocket
         if (webSocketService.getConnectionStatus()) {
            // Auto-join all existing rooms to receive real-time messages (non-blocking)
            // Note: joinRoom may timeout due to backend not emitting joined_room events
            Promise.all(
               roomsResult.rooms.map(async (room: ChatRoom) => {
                  try {
                     // Join the WebSocket room to receive real-time updates
                     // Use a race condition with a shorter timeout to reduce spam
                     await Promise.race([
                        webSocketService.joinRoom(room.id),
                        new Promise((_, reject) =>
                           setTimeout(() => reject(new Error('Join timeout')), 5000)
                        ),
                     ]);
                  } catch (err) {
                     // Silently handle join failures since backend may not support joined_room events
                     // Only log warnings for debugging, not errors
                     console.warn(`WebSocket join failed for room ${room.id} - continuing anyway`);
                  }
               })
            ).catch(() => {
               // Silently handle overall join failures
               console.warn(
                  'Some WebSocket room joins failed - this is expected if backend does not emit joined_room events'
               );
            });
         }

         // DON'T load messages for all rooms upfront - this is the main performance bottleneck
         // Messages will be loaded lazily when a room is selected
      } catch (err) {
         console.error('Failed to refresh channels:', err);
         setRoomsPagination((prev) => ({ ...prev, loading: false }));
         throw err;
      }
   }, [currentUser]);

   // Clear all cached data and force refresh
   const clearCacheAndRefresh = useCallback(async () => {
      try {
         setLoading(true);

         // Clear all local state
         setChannels([]);
         setMessages({});
         setTypingUsers({});
         setUnreadCount(0);
         setActiveChannel(null);

         // Clear WebSocket service cache and reconnect
         await webSocketService.clearCacheAndRefresh();

         // Refresh all data
         await refreshChannels();

         setConnectionStatus('connected');
      } catch (err) {
         console.error('Failed to clear cache and refresh:', err);
         setError(err instanceof Error ? err : new Error('Failed to refresh'));
         setConnectionStatus('error');
      } finally {
         setLoading(false);
      }
   }, [refreshChannels]);

   // Clean up invalid rooms
   const cleanupInvalidRooms = useCallback(async () => {
      try {
         await webSocketService.cleanupInvalidRooms();
         // Refresh channels after cleanup
         await refreshChannels();
      } catch (err) {
         console.error('Failed to cleanup invalid rooms:', err);
         setError(err instanceof Error ? err : new Error('Failed to cleanup'));
      }
   }, [refreshChannels]);

   // Create one-on-one chat
   const createOneOnOneChat = useCallback(
      async (userId: string): Promise<ChatRoom | null> => {
         try {
            const currentUserId = currentUser?.uid || '';

            // First, check if a chat already exists with this user
            const existingChat = channels.find((channel) => {
               // Check if it's a one-on-one chat with this specific user
               return (
                  channel.isPrivate &&
                  channel.members.length === 2 &&
                  channel.members.includes(currentUserId) &&
                  channel.members.includes(userId)
               );
            });

            if (existingChat) {
               return existingChat;
            }

            // Create new one-on-one chat
            const newChat = await webSocketService.createOneOnOneChat(userId);

            // Refresh channels to get the properly named chat from Firebase
            await refreshChannels();

            // Get the updated channels after refresh
            const updatedChannels = await webSocketService.getRooms();

            // Find the newly created chat in the refreshed channels
            const refreshedChat = updatedChannels.find((channel) => channel.id === newChat.id);

            return refreshedChat || newChat;
         } catch (error) {
            console.error('Error creating one-on-one chat:', error);
            setError(error as Error);
            return null;
         }
      },
      [currentUser, channels, refreshChannels]
   );

   // Create trip group chat
   const createTripGroupChat = useCallback(
      async (tripId: string, tripTitle: string): Promise<ChatRoom | null> => {
         try {
            // Use the WebSocket service to create the trip group chat
            const room = await webSocketService.createTripGroupChat(tripId, tripTitle);

            // Add to local channels if not already present
            setChannels((prev) => {
               const existing = prev.find((ch) => ch.id === room.id);
               if (!existing) {
                  return [...prev, room];
               }
               return prev;
            });

            return room;
         } catch (err) {
            console.error('Failed to create trip group chat:', err);
            throw err;
         }
      },
      []
   );

   // Create event group chat
   const createEventGroupChat = useCallback(async (eventId: string): Promise<ChatRoom | null> => {
      try {
         // Use the WebSocket service to create the event group chat
         const room = await webSocketService.createEventGroupChat(eventId);

         // Add to local channels if not already present
         setChannels((prev) => {
            const existing = prev.find((ch) => ch.id === room.id);
            if (!existing) {
               return [...prev, room];
            }
            return prev;
         });

         return room;
      } catch (err) {
         console.error('Failed to create event group chat:', err);
         throw err;
      }
   }, []);

   // Message actions
   const sendMessage = useCallback(async (roomId: string, text: string) => {
      if (!roomId || !text.trim()) {
         throw new Error('Room ID and message text are required');
      }

      setError(null); // Clear any previous errors

      try {
         await webSocketService.sendMessage(roomId, text.trim());
      } catch (err) {
         console.error('❌ Failed to send message in context:', err);
         const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
         setError(new Error(errorMessage));

         // Re-throw the error so the UI component can handle it
         throw new Error(errorMessage);
      }
   }, []);

   const editMessage = useCallback(
      async (messageId: string, newText: string, roomId?: string) => {
         try {
            // Use the active channel ID if available, otherwise try to find it
            const roomId = activeChannel?.id;
            await webSocketService.editMessage(messageId, newText, roomId);
         } catch (err) {
            console.error('Failed to edit message:', err);
            throw err;
         }
      },
      [activeChannel?.id]
   );

   const deleteMessage = useCallback(
      async (messageId: string, roomId?: string) => {
         try {
            // Use the active channel ID if available, otherwise try to find it
            const roomId = activeChannel?.id;
            await webSocketService.deleteMessage(messageId, roomId);
         } catch (err) {
            console.error('Failed to delete message:', err);
            throw err;
         }
      },
      [activeChannel?.id]
   );

   const reactToMessage = useCallback(
      async (messageId: string, emoji: string, roomId?: string) => {
         if (!messageId || !emoji) {
            throw new Error('Message ID and emoji are required');
         }

         setError(null); // Clear any previous errors

         try {
            // Use the active channel ID if available, otherwise try to find it
            const roomId = activeChannel?.id;
            await webSocketService.reactToMessage(messageId, emoji, roomId);
         } catch (err) {
            console.error('❌ Failed to react to message in context:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to add reaction';
            setError(new Error(errorMessage));
            throw new Error(errorMessage);
         }
      },
      [activeChannel?.id]
   );

   const removeReaction = useCallback(
      async (messageId: string, emoji: string, roomId?: string) => {
         if (!messageId || !emoji) {
            throw new Error('Message ID and emoji are required');
         }

         setError(null); // Clear any previous errors

         try {
            // Use the active channel ID if available, otherwise try to find it
            const roomId = activeChannel?.id;
            await webSocketService.removeReaction(messageId, emoji, roomId);
         } catch (err) {
            console.error('❌ Failed to remove reaction in context:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to remove reaction';
            setError(new Error(errorMessage));
            throw new Error(errorMessage);
         }
      },
      [activeChannel?.id]
   );

   // Typing indicators
   const startTyping = useCallback((roomId: string) => {
      webSocketService.startTyping(roomId);
   }, []);

   const stopTyping = useCallback((roomId: string) => {
      webSocketService.stopTyping(roomId);
   }, []);

   // Load more rooms for pagination
   const loadMoreRooms = useCallback(async () => {
      try {
         if (!currentUser || roomsPagination.loading || !roomsPagination.hasMore) {
            return;
         }

         setRoomsPagination((prev) => ({ ...prev, loading: true }));

         // Load more rooms using pagination
         const roomsResult = await webSocketService.getRoomsFromFirebase(
            currentUser.uid,
            20,
            roomsPagination.lastDoc
         );

         // Append new rooms to existing ones
         setChannels((prevChannels) => [...prevChannels, ...roomsResult.rooms]);

         // Update pagination state
         setRoomsPagination({
            hasMore: roomsResult.hasMore,
            lastDoc: roomsResult.lastDoc,
            loading: false,
         });

         // Load avatars for new rooms in the background (non-blocking)
         if (roomsResult.rooms.length > 0) {
            webSocketService
               .loadAvatarsForRooms(roomsResult.rooms, currentUser.uid)
               .then(() => {
                  // Update the channels state with the loaded avatars
                  setChannels((prevChannels) => [...prevChannels]); // Trigger re-render
               })
               .catch((err) => {
                  console.warn('Failed to load avatars for new rooms:', err);
               });
         }

         // Join WebSocket rooms if connected
         if (webSocketService.getConnectionStatus()) {
            await Promise.all(
               roomsResult.rooms.map(async (room: ChatRoom) => {
                  try {
                     // Use a race condition with shorter timeout to reduce spam
                     await Promise.race([
                        webSocketService.joinRoom(room.id),
                        new Promise((_, reject) =>
                           setTimeout(() => reject(new Error('Join timeout')), 5000)
                        ),
                     ]);
                  } catch (err) {
                     // Silently handle join failures
                     console.warn(`WebSocket join failed for room ${room.id} - continuing anyway`);
                  }
               })
            );
         }
      } catch (err) {
         console.error('Failed to load more rooms:', err);
         setRoomsPagination((prev) => ({ ...prev, loading: false }));
      }
   }, [currentUser, roomsPagination.loading, roomsPagination.hasMore, roomsPagination.lastDoc]);

   // Load room messages method
   const loadRoomMessages = useCallback(async (roomId: string) => {
      try {
         let roomMessages: ChatMessage[] = [];

         // Check if this is a one-on-one chat and load messages accordingly
         // Load without reactions by default for much faster performance
         if (roomId.startsWith('chat_') && roomId.split('_').length === 3) {
            const userIds = roomId.replace('chat_', '').split('_');
            if (userIds.length === 2) {
               roomMessages = await webSocketService.getMessagesBetweenUsers(
                  userIds[0],
                  userIds[1],
                  30, // Reduced from 50 for faster loading
                  false // Don't load reactions initially for better performance
               );
            } else {
               roomMessages = await webSocketService.getRoomMessages(
                  roomId,
                  1,
                  30, // Reduced from 50 for faster loading
                  false // Don't load reactions initially for better performance
               );
            }
         } else {
            roomMessages = await webSocketService.getRoomMessages(
               roomId,
               1,
               30, // Reduced from 50 for faster loading
               false // Don't load reactions initially for better performance
            );
         }

         // Update both context state and WebSocketService cache
         setMessages((prevMessages) => ({
            ...prevMessages,
            [roomId]: roomMessages,
         }));

         // Also update the WebSocketService's internal cache for reactions/edits/deletes
         webSocketService.updateMessageCache(roomId, roomMessages);
      } catch (err) {
         console.error(`Failed to load messages for room ${roomId}:`, err);
         setMessages((prevMessages) => ({
            ...prevMessages,
            [roomId]: [],
         }));
      }
   }, []);

   // Force reconnection
   const forceReconnection = useCallback(async () => {
      try {
         setLoading(true);
         setError(null);

         await webSocketService.forceReconnection();

         // Refresh channels after successful reconnection
         await refreshChannels();
      } catch (err) {
         console.error('Failed to force reconnection:', err);
         setError(err instanceof Error ? err : new Error('Failed to reconnect'));
         throw err;
      } finally {
         setLoading(false);
      }
   }, [refreshChannels]);

   // Load reactions for messages lazily (when user needs to see them)
   const loadReactionsForRoom = useCallback(
      async (roomId: string, messageIds?: string[]) => {
         try {
            const roomMessages = messages[roomId];
            if (!roomMessages || roomMessages.length === 0) {
               return;
            }

            // Load reactions for specific message IDs or all messages in the room
            const targetMessages = messageIds
               ? roomMessages.filter((msg) => messageIds.includes(msg.id))
               : roomMessages.slice(0, 20); // Limit to first 20 messages for performance

            const reactionPromises = targetMessages.map(async (message) => {
               try {
                  const reactionsSnapshot = await getDocs(
                     collection(db, 'messages', message.id, 'reactions')
                  );
                  const reactions: { [emoji: string]: string[] } = {};

                  reactionsSnapshot.forEach((reactionDoc) => {
                     const reaction = reactionDoc.data();
                     if (!reactions[reaction.emoji]) {
                        reactions[reaction.emoji] = [];
                     }
                     reactions[reaction.emoji].push(reaction.userId);
                  });

                  return { messageId: message.id, reactions };
               } catch (error) {
                  console.warn(`Failed to load reactions for message ${message.id}:`, error);
                  return { messageId: message.id, reactions: {} };
               }
            });

            const reactionResults = await Promise.all(reactionPromises);

            // Update messages with reactions
            setMessages((prevMessages) => {
               const updatedMessages = { ...prevMessages };
               if (updatedMessages[roomId]) {
                  const updatedRoomMessages = [...updatedMessages[roomId]];

                  reactionResults.forEach(({ messageId, reactions }) => {
                     const messageIndex = updatedRoomMessages.findIndex(
                        (msg) => msg.id === messageId
                     );
                     if (messageIndex !== -1) {
                        updatedRoomMessages[messageIndex] = {
                           ...updatedRoomMessages[messageIndex],
                           reactions,
                        };
                     }
                  });

                  updatedMessages[roomId] = updatedRoomMessages;
               }
               return updatedMessages;
            });

         } catch (error) {
            console.error(`Failed to load reactions for room ${roomId}:`, error);
         }
      },
      [messages]
   );

   // Load more messages for a room (pagination)
   const loadMoreMessages = useCallback(
      async (roomId: string, currentMessageCount: number): Promise<number> => {
         try {
            const currentMessages = messages[roomId] || [];
            if (currentMessages.length === 0) {
               return 0;
            }

            let additionalMessages: ChatMessage[] = [];

            // Calculate page offset based on current message count
            const pageSize = 20;
            const page = Math.ceil(currentMessageCount / pageSize) + 1;

            // Check if this is a one-on-one chat and load messages accordingly
            if (roomId.startsWith('chat_') && roomId.split('_').length === 3) {
               const userIds = roomId.replace('chat_', '').split('_');
               if (userIds.length === 2) {
                  // For one-on-one chats, we need to implement offset-based pagination
                  // This is a simplified approach - in production you'd use cursor-based pagination
                  const allMessages = await webSocketService.getMessagesBetweenUsers(
                     userIds[0],
                     userIds[1],
                     currentMessageCount + pageSize, // Load more than we have
                     false
                  );
                  // Get only the new messages
                  additionalMessages = allMessages.slice(currentMessageCount);
               }
            } else {
               // For group rooms, implement similar pagination
               const allMessages = await webSocketService.getRoomMessages(
                  roomId,
                  1,
                  currentMessageCount + pageSize,
                  false
               );
               // Get only the new messages
               additionalMessages = allMessages.slice(currentMessageCount);
            }

            if (additionalMessages.length > 0) {
               // Update messages by prepending new messages (since they're older)
               setMessages((prevMessages) => ({
                  ...prevMessages,
                  [roomId]: [...additionalMessages, ...currentMessages], // Older messages first
               }));
            }

            return additionalMessages.length;
         } catch (err) {
            console.error(`Failed to load more messages for room ${roomId}:`, err);
            return 0;
         }
      },
      [messages]
   );

   // Initialize connection when user is available
   useEffect(() => {
      if (currentUser) {
         initializeConnection();
      } else {
         setConnectionStatus('disconnected');
         setChannels([]);
         setMessages({});
         setActiveChannel(null);
         webSocketService.disconnect();
      }

      return () => {
         webSocketService.disconnect();
      };
   }, [currentUser, initializeConnection]);

   // Monitor connection status changes
   useEffect(() => {
      // Connection status monitoring - keeping minimal logging for now
   }, [connectionStatus]);

   // Context value
   const contextValue: WebSocketChatContextType = {
      // Connection state
      client: webSocketService, // For compatibility
      loading,
      error,
      connectionStatus,
      isFallbackMode: connectionStatus === 'error' || connectionStatus === 'disconnected',

      // Network status
      networkStatus,

      // Chat data
      channels,
      activeChannel,
      messages,
      onlineUsers: [], // This state is no longer managed here
      unreadCount,

      // Room pagination state
      roomsPagination,

      // Loading states
      loadingStates,

      // Actions
      setActiveChannel,
      refreshChannels,
      loadMoreRooms,
      clearCacheAndRefresh,
      cleanupInvalidRooms,
      forceReconnection,
      createOneOnOneChat,
      createTripGroupChat,
      createEventGroupChat,
      sendMessage,
      editMessage,
      deleteMessage,
      reactToMessage,
      removeReaction,
      loadRoomMessages,
      loadReactionsForRoom,
      getMessagesBetweenUsers: async (userId1: string, userId2: string): Promise<ChatMessage[]> => {
         try {
            return await webSocketService.getMessagesBetweenUsers(userId1, userId2, 50);
         } catch (err) {
            console.error('Failed to get messages between users:', err);
            throw err;
         }
      },

      // Typing indicators
      startTyping,
      stopTyping,
      typingUsers,

      // Loading state helpers
      isSendingMessage: (roomId: string) => {
         return loadingStates.sendingMessage[roomId] || false;
      },
      isAddingReaction: (messageId: string, emoji: string) => {
         return (
            (loadingStates.addingReaction[messageId] &&
               loadingStates.addingReaction[messageId][emoji]) ||
            false
         );
      },
      isRemovingReaction: (messageId: string, emoji: string) => {
         return (
            (loadingStates.removingReaction[messageId] &&
               loadingStates.removingReaction[messageId][emoji]) ||
            false
         );
      },

      // New method for loading more messages for pagination
      loadMoreMessages,

      // Online status helpers
      getUserOnlineStatus: async (userId: string): Promise<UserStatus | null> => {
         try {
            return await onlineStatusService.getUserOnlineStatus(userId);
         } catch (err) {
            console.error('Failed to get user online status:', err);
            return null;
         }
      },
      getMultipleUsersOnlineStatus: async (userIds: string[]): Promise<UserStatus[]> => {
         try {
            return await onlineStatusService.getMultipleUsersOnlineStatus(userIds);
         } catch (err) {
            console.error('Failed to get multiple users online status:', err);
            return [];
         }
      },
      isUserOnline: (userId: string): boolean => {
         return onlineStatusService.isUserOnline(userId);
      },
   };

   return (
      <WebSocketChatContext.Provider value={contextValue}>{children}</WebSocketChatContext.Provider>
   );
};

export default WebSocketChatContext;
